/**
 * DTAvailability — single source of truth for in-stock availability/shipping messaging
 * across all three PDP layouts (standard, grid, explosive) and the cart.
 *
 * Replaces the old duplicated logic (custom-script.js `checkStockAvailability`, the
 * `#availability-text` copies in explosive.js/gridpdp.js, and the day-based calc in
 * snippets/product-availability-text.liquid).
 *
 * Message precedence for an IN-STOCK variant:
 *   1. Per-variant dropship metafield message (dropship.availability .message), written
 *      by yo_skubana — rendered verbatim.
 *   2. Transitional fallback: if the product is still tagged `dropship` but has no
 *      metafield message yet, show the legacy "Ships In 2-3 Days" so un-migrated
 *      products don't regress. Remove once migration is confirmed complete.
 *   3. Day-based default ("Ships Today / Tomorrow / <Weekday>") computed live in STORE
 *      time (America/Los_Angeles) from theme settings (cutoff hour + non-ship days/dates).
 *
 * Out-of-stock variants return '' — the resolver never touches the OOS / "Notify Me" flow.
 *
 * Inputs come from Liquid:
 *   window.DT_SHIP_CONFIG      = { cutoffHour, nonshipDays, nonshipDates }  (theme settings)
 *   window.DT_PRODUCT_DROPSHIP = <bool>                                     (product.tags contains 'dropship')
 *   window.DT_VARIANT_AVAIL    = { <variantId>: { available, message } }    (per variant)
 */
window.DTAvailability = (function () {
  var WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  var WEEKDAY_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var LEGACY_DROPSHIP_MESSAGE = 'In Stock - Ships In 2-3 Days';
  var STORE_TIMEZONE = 'America/Los_Angeles';

  function getConfig() {
    var c = window.DT_SHIP_CONFIG || {};
    return {
      cutoffHour: (typeof c.cutoffHour === 'number') ? c.cutoffHour : 14,
      nonshipDays: (c.nonshipDays == null ? '' : String(c.nonshipDays)),
      nonshipDates: (c.nonshipDates == null ? '' : String(c.nonshipDates))
    };
  }

  // Current weekday (0-6), hour (0-23) and YYYY-MM-DD in STORE time, regardless of the
  // visitor's own timezone. There is one cutoff (2pm Pacific = 5pm Eastern) for everyone.
  function storeNowParts() {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: STORE_TIMEZONE,
      weekday: 'short', hour: 'numeric', hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    var hour = parseInt(parts.hour, 10) % 24; // some engines emit "24" at midnight
    return {
      day: WEEKDAY_INDEX[parts.weekday],
      hour: hour,
      date: parts.year + '-' + parts.month + '-' + parts.day
    };
  }

  // Match Liquid `contains` on a comma/space separated string, but token-safe so "6"
  // never matches inside "16", etc.
  function listContains(listStr, token) {
    var cleaned = ',' + String(listStr).replace(/\s+/g, '') + ',';
    return cleaned.indexOf(',' + token + ',') !== -1;
  }

  // Calendar-day arithmetic done in UTC on the store's Y/M/D so it is DST-safe: we only
  // care about which calendar day / weekday results, not any wall-clock instant.
  function addCalendarDays(dateStr, n) {
    var y = parseInt(dateStr.slice(0, 4), 10);
    var m = parseInt(dateStr.slice(5, 7), 10);
    var d = parseInt(dateStr.slice(8, 10), 10);
    var dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + n);
    var mm = ('0' + (dt.getUTCMonth() + 1)).slice(-2);
    var dd = ('0' + dt.getUTCDate()).slice(-2);
    return { date: dt.getUTCFullYear() + '-' + mm + '-' + dd, day: dt.getUTCDay() };
  }

  function isShippingDay(cand, cfg) {
    if (listContains(cfg.nonshipDays, String(cand.day))) return false;
    if (listContains(cfg.nonshipDates, cand.date)) return false;
    return true;
  }

  // "In Stock - Ships Today / Tomorrow / <Weekday>", honoring cutoff + non-ship days/dates.
  function defaultMessage() {
    var cfg = getConfig();
    var now = storeNowParts();
    var canShipToday = isShippingDay({ date: now.date, day: now.day }, cfg) && now.hour < cfg.cutoffHour;
    if (canShipToday) return 'In Stock - Ships Today';

    for (var n = 1; n <= 14; n++) {
      var cand = addCalendarDays(now.date, n);
      if (isShippingDay(cand, cfg)) {
        if (n === 1) return 'In Stock - Ships Tomorrow';
        return 'In Stock - Ships ' + WEEKDAY_NAME[cand.day];
      }
    }
    return 'In Stock'; // safety net; should never be reached
  }

  // variant: { available: <bool>, message: <string|null> }
  function resolve(variant) {
    variant = variant || {};
    if (!variant.available) return '';
    if (variant.message != null && String(variant.message).trim() !== '') {
      return String(variant.message);
    }
    if (window.DT_PRODUCT_DROPSHIP) return LEGACY_DROPSHIP_MESSAGE; // transitional
    return defaultMessage();
  }

  // Convenience: resolve by variant id using the injected map.
  function resolveById(variantId) {
    var map = window.DT_VARIANT_AVAIL || {};
    return resolve(map[variantId] || map[String(variantId)] || {});
  }

  return {
    resolve: resolve,
    resolveById: resolveById,
    defaultMessage: defaultMessage
  };
})();
